import { Select } from 'antd';
// eslint-disable-next-line max-len
import ReferenceDataDropdown from 'common/reference-data/containers/reference-data-dropdown-container';
import Table from 'common/table/table';
import { Icons, RowIconAction } from 'common/table/table-helper';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import { FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Div, Icon, InputNumber } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { dotAlternative, typeOfPartList } from '../../../utils/app-configs';
import PartlistAttributes from './part-list-attributes';
import SearchPart from './search-part';
import SupersededList from './superseded-list';

const Wrapper = styled(Div)`
  font-size: 12px;
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  padding-left: 0px;
`;

const StyledIcon = styled(Icon)`
  font-size: 15px;
  margin-right: 5px;
  --fa-secondary-color: #7e7e7e;
  --fa-primary-color: #6bc93c;
  --fa-secondary-opacity: 1;
  color: #7e7e7e;
`;

const WarningText = styled(Div)`
  text-transform: uppercase;
  font-weight: bold;
  color: ${p => p.theme.colors.alerts[2]};
  i {
      margin-right: 5px;
      color: ${p => p.theme.colors.alerts[2]};
    }
`;

const ErrorIcon = styled(Icon)`
  color: ${p => p.theme.colors.publishSteps.error};
  margin-right: 5px;
`;

const WarningIcon = styled(Icon)`
  color: ${p => p.theme.colors.publishSteps.warning};
  margin-right: 5px;
`;

const ErrorSpan = styled.span`
  color: ${p => p.theme.colors.publishSteps.error};
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
`;

const SupersededMessage = styled.span`
  color: ${p => p.theme.colors.publishSteps.warning};
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  display: block;
`;

const QuantityWrapper = styled(Div)`
  .ant-input-number {
    height: 25px !important;

    input {
      height: 22px !important;
    }
  }
`;

const FrozenMessage = styled(Div)`
  font-size: 1.2rem;
  padding: 16px 8px;

  > i {
    margin-right: 5px;
  }
`;

const StyledSelect = styled(Select)`
  font-size: 14px;
`;

const StyledDiv = styled(Div)`
  width: 100% !important;
  div {
    width: 100%;
  }
`;

const PartList = ({
  partList,
  onShowDetails,
  intl,
  form,
  orderId,
  disabled,
  handleUpdateParts,
  handleRemovePart,
  handleAddPart,
  shipTo,
  isFrozen,
  parts,
  decimalMask,
  checkUpdate = false,
  serviceCenterId,
  openSupersededParts,
  handleRemoveSupersededPart,
  handleAddOfSupersededPart,
  partListType = null,
}) => {
  const [selectedMaterials, setSelectedMaterials] = useState(null);
  const [materialNumbers, setMaterialNumbers] = useState(null);

  const refData = useSelector(state => state.referenceData.data);
  const logisticsFlow = refData[`${serviceCenterId}_order_logisticsFlow`] || [];

  const isXML = partListType === typeOfPartList.xmlView;

  useDeepCompareEffect(() => {
    const partsInSupersededState = parts
      ?.filter(p => p?.supersededBy?.length > 0)
      ?.map(pa => ({
        materialNumber: pa.materialNumber,
        materialDescription: pa.materialDescription,
      }));
    if (openSupersededParts) {
      openSupersededParts(!!partsInSupersededState?.length);
    }
  }, [parts]);

  // If disabled, part list is frozen, part is frozen or part is deletable when FF is enabled
  const isPartDisabled = currentPart => disabled ||
    (isToggleEnabled('deletableParts') ? !currentPart?.isDeletable : currentPart?.isFrozen) ||
    isFrozen;

  const getColumns = () => [
    {
      id: 'actions',
      displayName: '',
      fieldName: '',
      width: isXML ? '30px' : '60px',
      renderFn: r => <Icons>
        {!isXML && <RowIconAction
          title={intl.formatMessage({ id: 'order.tooltip.viewPart' })}
          tooltipKey='eye'
          iconKey='eye'
          action={() => onShowDetails(r)}
          disabled={false}
        />}
        <RowIconAction
          title={intl.formatMessage({ id: 'order.tooltip.deletePart' })}
          tooltipKey={'delete'}
          iconKey={isPartDisabled(r) ? 'trash-can-slash' : 'trash-alt'}
          action={() => handleRemovePart(r)}
          disabled={isPartDisabled(r)}
        />
      </Icons>,
    },
    {
      id: 'materialNumber',
      displayName: intl.formatMessage({ id: 'order.parts.MATERIAL_NO' }),
      fieldName: 'materialNumber',
      width: '120px',
    },
    {
      displayName: intl.formatMessage({ id: 'order.parts.MATERIAL_DESC' }),
      fieldName: 'materialDescription',
      id: 'materialDescription',
      width: '230px',
      tooltip: row => row.materialDescription,
    },
    {
      id: 'quantityOrdered',
      displayName: intl.formatMessage({ id: 'order.parts.QTY_ORDERED' }),
      fieldName: 'quantityOrdered',
      width: '100px',
      tooltip: row => isFrozen || row.isFrozen || row?.supersededBy?.length > 0 ?
        <span>
          <FormattedNumber value={row.quantityOrdered} />
          {row.quantityOrderedUnit}
        </span> :
        null,
      renderFn: (part) => {
        const partId = part.id.replace('.', dotAlternative);
        const quantityValidator = form.getFieldDecorator(`parts.${partId}.quantityOrdered`, {
          initialValue: part.quantityOrdered,
          rules: [{
            required: true,
            message: intl.formatMessage({ id: 'rules.QUANTITY' }),
          }],
        });

        if (isFrozen || part.isFrozen || part?.supersededBy?.length > 0) {
          return (
            <span>
              <FormattedNumber value={part.quantityOrdered} />
              {part.quantityOrderedUnit}
            </span>
          );
        }
        return (
          <QuantityWrapper onClick={e => e.stopPropagation()}>
            {quantityValidator(
              <StyledInputNumber
                min={1}
                hasError={!disabled && (!part.quantityOrdered || part.quantityOrdered <= 0)}
                disabled={disabled}
              />,
            )}
          </QuantityWrapper>
        );
      },
    },
    {
      id: 'cost',
      displayName: intl.formatMessage({ id: 'order.parts.COST' }),
      fieldName: 'cost',
      width: '120px',
      tooltip: (part) => {
        if (!Number.isFinite(part.cost)) {
          return null;
        }

        if (part.costCurrency) {
          const number = intl.formatNumber(part.cost, {
            style: 'currency', //eslint-disable-line
            currency: part.costCurrency,
          });
          if (!decimalMask) {
            return number.replace(/\.00$/, '').replace(/^(\D+)/, '$1 ');
          }
          return number.replace(/^(\D+)/, '$1 ');
        }
        return <FormattedNumber value={part.cost} />;
      },
      renderFn: (part) => {
        if (!Number.isFinite(part.cost)) {
          return null;
        }

        if (part.costCurrency) {
          const number = intl.formatNumber(part.cost, {
            style: 'currency', //eslint-disable-line
            currency: part.costCurrency,
          });
          if (!decimalMask) {
            return number.replace(/\.00$/, '').replace(/^(\D+)/, '$1 ');
          }
          return number.replace(/^(\D+)/, '$1 ');
        }
        return <FormattedNumber value={part.cost} />;
      },
    },
    {
      id: 'weight',
      displayName: intl.formatMessage({ id: 'order.parts.WEIGHT' }),
      fieldName: 'weight',
      width: '90px',
      tooltip: part => `${part?.weight || ''}${part?.weightUnit ? ` ${part.weightUnit}` : ''}`,
      renderFn: part => `${part?.weight || ''}${part?.weightUnit ? ` ${part.weightUnit}` : ''}`,
    },
    {
      id: 'vendorName',
      displayName: intl.formatMessage({ id: 'order.parts.VENDOR' }),
      fieldName: 'vendorName',
      width: '200px',
      tooltip: row => row.vendorName,
    },
    {
      id: 'logisticFlow',
      displayName: intl.formatMessage({ id: 'order.parts.LOGISTIC_FLOW' }),
      fieldName: 'logisticFlow',
      width: '120px',
      tooltip: row => isFrozen || row.isFrozen || row?.supersededBy?.length > 0 ?
        row.logisticFlowName :
        null,
      renderFn: (part) => {
        const partId = part.id.replace('.', dotAlternative);
        const logiscticsFlowValidator = form.getFieldDecorator(`parts.${partId}.logisticFlow`, {
          initialValue: part.logisticFlow,
        });
        if (isFrozen || part.isFrozen || part?.supersededBy?.length > 0) {
          return part.logisticFlowName;
        }

        return (
          <StyledDiv onClick={e => e.stopPropagation()}>
            {logiscticsFlowValidator(
              <ReferenceDataDropdown
                disabled={disabled}
                placeholder={intl.formatMessage({
                  id: 'order.parts.SELECT_DELIVERING_PLANT',
                })}
                type='order'
                name='logisticsFlow'
                valueKey='code'
                valueLabel='description'
                serviceCenterId={serviceCenterId}
                useBodyAsParent={true}
                dropdownMatchSelectWidth={false}
              />,
            )}
          </StyledDiv>
        );
      },
    },
    isToggleEnabled('logisticPlants') && {
      id: 'plant',
      displayName: intl.formatMessage({ id: 'order.parts.PLANT' }),
      fieldName: 'plant',
      width: '120px',
      renderFn: (part) => {
        if (logisticsFlow.length) {
          const partId = part?.id?.replace('.', dotAlternative);
          const currentLogisticsFlow = logisticsFlow?.find(l =>
            l.code === form.getFieldValue(`parts.${partId}.logisticFlow`));
          const plantsOfCurrentLogisticsFlowOption = currentLogisticsFlow?.plants?.map(p => ({
            value: p.id,
            label: `${p.id} - ${p.name}`,
            default: p.isDefault,
          }));

          const deliveringPlantValidator = form.getFieldDecorator(`parts.${partId}.plant`,
            {
              initialValue: plantsOfCurrentLogisticsFlowOption?.length === 1 ?
                part.plant ||
                plantsOfCurrentLogisticsFlowOption[0]?.value
                :
                part.plant ||
                plantsOfCurrentLogisticsFlowOption?.find(d => d.default === true)?.value,
            });

          if (isFrozen || part.isFrozen || part?.supersededBy?.length > 0) {
            return part.plant;
          }
          return (
            plantsOfCurrentLogisticsFlowOption?.length !== 0 ?
              <StyledDiv onClick={e => e.stopPropagation()}>
                {plantsOfCurrentLogisticsFlowOption.length > 1 ?
                  deliveringPlantValidator(
                    <StyledSelect
                      options={plantsOfCurrentLogisticsFlowOption}
                      placeholder={intl.formatMessage({
                        id: 'order.parts.SELECT_DELIVERING_PLANT',
                      })}
                      disabled={disabled}
                      dropdownMatchSelectWidth={false}
                    />,
                  )
                  : plantsOfCurrentLogisticsFlowOption[0]?.value
                }
              </StyledDiv> : null
          );
        }
        return null;
      },
    },
    {
      // important that this will be fully displayed
      id: 'poNumber',
      displayName: intl.formatMessage({ id: 'order.parts.PO_NO' }),
      fieldName: 'poNumber',
      width: '150px',
      tooltip: row => row.poNumber,
      isHidden: isXML,
    },
    {
      id: 'LogisticsDate',
      displayName: intl.formatMessage({
        id: 'order.parts.LOGISTICS_DATE',
      }),
      fieldName: 'LogisticsDate',
      width: '250px',
      isHidden: isXML,
      renderFn: part => <Div>
        <StyledIcon className={part?.logisticsDate?.icon} />
        {part?.logisticsDate?.fullDescription}
      </Div>,
    },
  ];

  const getPartById = id => parts?.find(p => p.id === id);

  const handleRemoveOriginalPart = (partId) => {
    const toDeletePart = getPartById(partId);
    if (toDeletePart) {
      handleRemovePart(toDeletePart);
    }
  };

  const getGtsError = part => <ErrorSpan>
    <ErrorIcon type='arrow-turn-down-right' />
    <ErrorIcon type='triangle-exclamation' />
    <span>
      {/* eslint-disable max-len */}
      {`GTS block active (Type: ${part.gtsBlockedObjectType} – Object: ${part.gtsBlockedObjectNumber} – Status: ${part.gtsBlockStatuses?.map(p => p.statusText).join(', ')})`}
    </span>
  </ErrorSpan>;

  const getSupersededList = (row) => {
    const originalColumns = getColumns()?.filter(c =>
      ['materialNumber', 'materialDescription', 'cost', 'weight', 'vendorName'].includes(c.id));
    const superSededCol = [...originalColumns,
    {
      id: 'samePart',
      displayName: '',
      fieldName: '',
      width: '400px',
      renderFn: (part) => {
        if (part.samePart) {
          return <WarningText>
            <Icon type={'triangle-exclamation'} />
            {intl.formatMessage({ id: 'order.parts.supersededParts.SAME_PART' })}
          </WarningText>;
        }
        return null;
      },
    }];

    const supersededPartList = row?.supersededBy?.map((p) => {
      if (p.materialNumber === row.materialNumber) {
        return { ...p, samePart: true };
      }
      return p;
    });

    return <SupersededList
      columns={superSededCol}
      originalPartId={row?.id}
      originalPartMaterialNumber={row?.materialNumber}
      hasCyclicReference={row?.hasCyclicReference}
      autoSuperseded={row?.autoReplacementInfo}
      isObsolete={row?.isObsolete}
      supersededList={supersededPartList}
      partListType={partListType}
      handlePartAdd={(supersededPart, originalPartId) =>
        handleAddOfSupersededPart(supersededPart, getPartById(originalPartId))}
      handleRemovePart={handleRemoveOriginalPart}
      handleRemoveSupersededPart={handleRemoveSupersededPart}
    />;
  };

  const isSuperseded = row =>
    row?.supersededBy?.length
    || row?.autoReplacementInfo?.originalPartNumber
    || row?.hasCyclicReference
    || row?.isObsolete;

  const getSupersededmsgValue = row => row?.supersededMsg;

  const getSupersededmsg = (row) => {
    const supersededmsg = getSupersededmsgValue(row);
    if (!supersededmsg) {
      return null;
    }

    return <SupersededMessage>
      <WarningIcon type='arrow-turn-down-right' />
      <WarningIcon type='triangle-exclamation' />
      <span>{supersededmsg}</span>
    </SupersededMessage>;
  };

  const getExpandedRow = row => <>
    {getSupersededmsg(row)}
    {row.gtsBlockStatuses?.length ? getGtsError(row) : null}
    {isSuperseded(row) ? getSupersededList(row) : null}
  </>;

  const allPOFinished = parts?.every(p => p.poNumber);

  return (
    <Wrapper>
      {!isXML && <PartlistAttributes
        orderId={orderId}
        form={form}
        partList={partList}
        disabled={disabled}
        shipTo={shipTo}
      />}
      <Table
        labelInColumn={true}
        columns={getColumns()}
        data={parts}
        totalDataCount={isFrozen ? 10 : parts?.length}
        renderCustomFooter={isFrozen ? null : () => <SearchPart
          handleUpdateParts={handleUpdateParts}
          checkUpdate={checkUpdate}
          handleAddPart={handleAddPart}
          selectedMaterials={selectedMaterials}
          setSelectedMaterials={setSelectedMaterials}
          materialNumbers={materialNumbers}
          setMaterialNumbers={setMaterialNumbers}
          disabled={disabled}
          serviceCenterId={serviceCenterId}
        />}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys: parts?.filter(p =>
            !!getSupersededmsgValue(p) ||
            p.gtsBlockStatuses?.length ||
            isSuperseded(p),
          )?.map(p => p.id),
          expandedRowRender: row => getExpandedRow(row),
        }}
        hasCustomHeader={true}
        fullWidth={true}
        height='100%'
        maxHeight={isXML ? '200px' : null}
      />
      {isFrozen && !allPOFinished &&
        <FrozenMessage>
          <Icon type='info-circle' />
          <FormattedMessage id='order.parts.FROZEN_MSG' />
        </FrozenMessage>
      }
    </Wrapper>
  );
};

PartList.propTypes = {
  partList: PropTypes.object,
  onShowDetails: PropTypes.func,
  intl: PropTypes.object.isRequired,
  form: PropTypes.object,
  orderId: PropTypes.string,
  disabled: PropTypes.bool,
  handleUpdateParts: PropTypes.func,
  handleRemovePart: PropTypes.func,
  handleAddPart: PropTypes.func,
  shipTo: PropTypes.object,
  isFrozen: PropTypes.bool,
  parts: PropTypes.array,
  decimalMask: PropTypes.bool,
  checkUpdate: PropTypes.bool,
  serviceCenterId: PropTypes.string,
  openSupersededParts: PropTypes.func,
  handleRemoveSupersededPart: PropTypes.func,
  handleAddOfSupersededPart: PropTypes.func,
  partListType: PropTypes.string,
};

export default injectIntl(PartList);
